import { Link, useParams } from 'react-router-dom'
import { AiFillHome } from 'react-icons/ai'
import {
  MdCheckCircle,
  MdOutlineKeyboardDoubleArrowRight,
} from 'react-icons/md'
import { useEffect, useState } from 'react'
import { fetchPractices, PracticeCategory } from '../../services/practices'
import {
  IoIosWarning,
  IoMdArrowDropleftCircle,
  IoMdArrowDroprightCircle,
  IoMdMail,
  IoMdRefresh,
} from 'react-icons/io'
import {
  FaGlobe,
  FaPenFancy,
  FaRegStar,
  FaSms,
  FaStar,
  FaWhatsapp,
} from 'react-icons/fa'
import { FaLocationDot, FaPhone } from 'react-icons/fa6'
import {
  PracticesWarnings,
  hasPracticesWarnings,
} from '../components/PracticesWarnings'
import { useTranslation } from 'react-i18next'
import { Announce } from '../interfaces/announce'
import { User, UserProfile, GalleryItem } from '../interfaces/user'
import { fetchAnnounceDetails } from '../../services/announces'
import { fetchUsers } from '../../services/fetchAllUser'
import formatBelgianPhoneNumber from '../fonctions/formatBelgiumPhone'
import calculateAge from '../fonctions/calculateAge'
import Map from '../components/Map'
import Tooltip from '../components/MyTooltip'
import Gallery from '../components/Gallery'
import { handleFavoriteClick } from '../fonctions/addFavorite'
import { TbDisabled2 } from 'react-icons/tb'
import Cookies from 'js-cookie'
import { updateSearchFiltersAndRedirect } from '../fonctions/updateSearchFiltersAndRedirect'
import SendMailModal from '../components/SendMailModal'
import ReportUserModal from '../components/ReportUserModal'

if (typeof global === 'undefined') {
  window.global = window
}

const dayMap: { [key: string]: number } = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 0,
}

function OneAnnounce() {
  const { t } = useTranslation()
  let { id } = useParams()
  const [announce, setAnnounce] = useState<Announce | null>(null)
  const [isAtBottom, setIsAtBottom] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [gallery, setGallery] = useState<GalleryItem[]>([])
  const [practiceData, setPracticeData] = useState<PracticeCategory[]>([])
  const [, setSelectedProvinces] = useState<string[]>([])
  const [, setPracticeNames] = useState<string[]>([])
  const [isFavorite, setIsFavorite] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [isSendMailModalOpen, setIsSendMailModalOpen] = useState(false)
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const params = useParams<{ id: string; gender: string; statuses: string }>()

  const hasPrices =
    (announce?.price?.length ?? 0) > 0 &&
    announce?.timing?.length !== undefined &&
    announce?.timing?.length > 0
  const hasEscortPrices =
    (announce?.priceEscort?.length ?? 0) > 0 &&
    announce?.timingEscort?.length !== undefined &&
    announce?.timingEscort?.length > 0
  const [tooltip, setTooltip] = useState<{
    province: string
    x: number
    y: number
  } | null>(null)

  const formatURL = (title: string, id: number) => {
    return `${title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')}/${id}`
  }

  const getPreviousSlug = () => {
    if (!announce) return ''
    return (
      formatGender(user?.userProfile?.gender || '') +
      '/' +
      formatStatus(announce.status || []) +
      '/' +
      formatURL(announce.title, Number(announce.id) - 1)
    )
  }

  const getNextSlug = () => {
    if (!announce) return ''
    return (
      formatGender(user?.userProfile?.gender || '') +
      '/' +
      formatStatus(announce.status || []) +
      '/' +
      formatURL(announce.title, Number(announce.id) + 1)
    )
  }

  const formatGender = (gender: string) => {
    switch (gender) {
      case 'woman':
        return 'femme'
      case 'man':
        return 'homme'
      case 'transgender':
        return 'transgenre'
      case 'couple':
        return 'couple'
      default:
        return gender
    }
  }

  const formatStatus = (statuses: string[]) => {
    return statuses
      .map(status => {
        switch (status) {
          case 'libertins ou visiteur':
            return 'libertins-visiteurs'
          case 'private':
            return 'prive'
          case 'escort':
            return 'escort'
          case 'massage':
            return 'massage'
          case 'maison privee':
            return 'maison-privee'
          case 'bars et clubs prive':
            return 'bars-clubs-prives'
          case 'salons de massage':
            return 'salons-massage'
          case 'hotels coquins':
            return 'hotels-coquins'
          case 'saunas prives':
            return 'saunas-prives'
          case 'clubs echangistes':
            return 'clubs-echangistes'
          default:
            return status
        }
      })
      .join('-')
  }

  const onProvinceHover = (province: string, event: React.MouseEvent) => {
    const { clientX: x, clientY: y } = event
    setTooltip({ province, x, y })
  }

  const onMouseLeave = () => {
    setTooltip(null)
  }

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const scrollHeight = document.documentElement.scrollHeight
      const clientHeight = document.documentElement.clientHeight
      const isBottom = scrollTop + clientHeight >= scrollHeight

      setIsScrolled(scrollTop > 0 && !isBottom)
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    const fetchAnnounceData = async () => {
      if (!params.id) {
        setErrorKey('back.Failed to fetch announce')
        return
      }

      setErrorKey(null)
      setAnnounce(null)
      setGallery([])
      setUser(null)

      try {
        const data = await fetchAnnounceDetails(params.id)
        setAnnounce(data.announce)
        setGallery(data.gallery)

        const users = await fetchUsers()
        const foundUser = users.find((u: User) => u.id === data.announce.userId)
        setUser(foundUser ?? null)

        try {
          const favorites = JSON.parse(Cookies.get('MyFavorites') || '{}')
          setIsFavorite(!!favorites[params.id])
        } catch (e) {
          console.error('Error parsing favorites:', e)
        }
      } catch (error) {
        setErrorKey('back.Failed to fetch announce')
        console.error('Error fetching announce:', error)
      }
    }

    fetchAnnounceData()
  }, [params.id])

  useEffect(() => {
    const getPractices = async () => {
      if (!announce) {
        return
      }

      try {
        const data = await fetchPractices()
        setPracticeData(data)

        const practiceIds = announce?.practices || []
        const names = practiceIds
          .map(id => {
            for (const category of data) {
              const practice = category.practices.find(
                practice => practice.id === id,
              )
              if (practice) {
                return practice.practiceName
              }
            }
            return null
          })
          .filter(name => name !== null)

        setPracticeNames(names)
      } catch (error) {
        console.error('Error fetching practices:', error)
      }
    }

    getPractices()
  }, [announce])

  const handleLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    event.preventDefault()
    if (user?.address?.city) {
      updateSearchFiltersAndRedirect(user.address.city)
    }
  }

  const userProfileFields = [
    { key: 'gender', label: 'create-ad.gender', usePrefix: true },
    { key: 'age', label: 'create-ad.age', usePrefix: false },
    {
      key: 'orientation',
      label: 'create-ad.sexual-orientation',
      usePrefix: true,
    },

    { key: 'size', label: 'create-ad.size', usePrefix: false },
    { key: 'weight', label: 'create-ad.weight', usePrefix: false },
    { key: 'hairColor', label: 'create-ad.hair', usePrefix: true },
    { key: 'eyeColor', label: 'create-ad.eyes', usePrefix: true },
    {
      key: 'nationality',
      label: 'create-ad.nationality',
      usePrefix: true,
      prefix: 'nationalities',
    },
    { key: 'ethnicity', label: 'create-ad.ethnicity', usePrefix: true },
    { key: 'braCup', label: 'create-ad.breasts', usePrefix: true },
    { key: 'penisSize', label: 'create-ad.penis-size', usePrefix: false },
    {
      key: 'intimateHairCut',
      label: 'create-ad.intimate-haircut',
      usePrefix: true,
    },
    { key: 'tatoo', label: 'create-ad.tatoo', usePrefix: true },
    { key: 'smoker', label: 'create-ad.smoker', usePrefix: true },
    { key: 'piercing', label: 'create-ad.piercings', usePrefix: true },
    { key: 'alcohol', label: 'create-ad.alcohol', usePrefix: true },
    {
      key: 'languages',
      label: 'create-ad.spoken-languages',
      usePrefix: true,
      prefix: 'languages',
    },
  ]

  const coverImage = gallery.find(item => item.cover === true)

  const isWithinTimeRange = (
    currentTime: string,
    start: string,
    end: string,
  ) => {
    const [currentHour, currentMin] = currentTime.split(':').map(Number)
    const [startHour, startMin] = start.split(':').map(Number)
    const [endHour, endMin] = end.split(':').map(Number)

    const current = currentHour * 60 + currentMin
    const startTime = startHour * 60 + startMin
    const endTime = endHour * 60 + endMin

    return current >= startTime && current <= endTime
  }

  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  return (
    <div className=''>
      <div className='bg-redpink'>
        <div className='max-w-7xl px-2 lg:px-0 pb-2 mx-auto font-light flex flex-row justify-between '>
          <div className='flex items-center pt-2 text-sm lg:text-base gap-2 sm:gap-0 flex-wrap sm:flex-nowrap'>
            <Link
              to='/'
              className='text-white hover:text-anthracite transition-all'
            >
              <AiFillHome className='text-xl sm:text-2xl' />
            </Link>
            <MdOutlineKeyboardDoubleArrowRight className='mx-1 sm:mx-2 text-white text-xl sm:text-2xl hidden sm:block' />
            {Array.isArray(announce?.status) &&
              [...new Set(announce.status)].map((status, index) => (
                <div key={index} className='flex items-center'>
                  <Link
                    to={`/${status}`}
                    className='text-white hover:text-anthracite transition-all'
                  >
                    {t(`create-ad.${status}`)}
                  </Link>
                  <MdOutlineKeyboardDoubleArrowRight className='mx-1 sm:mx-2 text-white text-xl sm:text-2xl hidden sm:block' />
                </div>
              ))}
            <p className='text-white hidden sm:block mt-0'>{announce?.title}</p>
          </div>
          <div className='flex mt-2'>
            <Link to={`/announce/${getPreviousSlug()}`}>
              <IoMdArrowDropleftCircle className='text-3xl sm:text-4xl hover:text-white transition-all' />
            </Link>
            <Link to={`/announce/${getNextSlug()}`}>
              <IoMdArrowDroprightCircle className='text-3xl sm:text-4xl hover:text-white transition-all' />
            </Link>
          </div>
        </div>
      </div>

      <div className='bg-anthracite'>
        <div className='max-w-7xl mx-auto text-xs lg:text-base font-light flex justify-between text-white py-3'>
          <div className='flex'>
            <div className='flex'>
              {user && (
                <>
                  <span className='hidden lg:flex'>
                    <FaPenFancy className='text-redpink text-base lg:text-xl mr-2' />
                    {t('posted-by')}{' '}
                    <Link
                      to={`/search/user/${user.id}`}
                      className='text-redpink font-semibold pl-2 hover:underline transition-all'
                    >
                      {user.nickname}
                    </Link>
                    {user.birthday && (
                      <span className='pl-2'>
                        ({calculateAge(user.birthday)} {t('years-old')})
                      </span>
                    )}
                  </span>
                  <button
                    className='text-redpink font-semibold flex hover:underline ml-2 lg:ml-3'
                    onClick={() => setIsReportModalOpen(true)}
                  >
                    <IoIosWarning className='text-base lg:text-xl mr-1' />{' '}
                    {t('report-user')}
                  </button>
                </>
              )}
            </div>
          </div>
          <div className='flex'>
            <button
              onClick={event => {
                if (announce?.id) {
                  handleFavoriteClick(event, announce.id, setIsFavorite)
                }
              }}
              className='flex items-center hover:underline mr-2 sm:mr-0'
            >
              {isFavorite ? (
                <FaStar className='pr-1 transition-all text-yellow-400 text-lg  lg:text-2xl group-hover:text-white group-hover:transition-all' />
              ) : (
                <FaRegStar className='pr-1 text-lg  lg:text-2xl' />
              )}
              {t('add-to-favorites')}
            </button>
            {announce?.emailOnly && (
              <Link
                to='#'
                className='hidden md:flex hover:underline ml-3'
                onClick={e => {
                  e.preventDefault()
                  if (user?.email) {
                    setIsSendMailModalOpen(true)
                  }
                }}
              >
                <IoMdMail className='text-blue-500 mr-2 text-lg  lg:text-2xl' />
                {t('send-mail')}
              </Link>
            )}

            <a
              href={`tel:${user?.phone}`}
              className='hidden sm:flex hover:underline ml-4 mr-2 lg:mr-0'
            >
              <FaPhone className='text-emerald-500 mr-2 text-base lg:text-xl' />
              {formatBelgianPhoneNumber(user?.phone ?? '')}
            </a>

            {errorKey ? (
              <div className='hidden'></div>
            ) : (
              <div
                className={`fixed bottom-0 left-0 right-0 flex justify-between py-2 px-4 sm:hidden z-30 transition-transform duration-300 ${
                  isScrolled && !isAtBottom
                    ? 'translate-y-0'
                    : 'translate-y-full'
                }`}
              >
                <a
                  href={`https://wa.me/${user?.phone}`}
                  className='w-1/2 bg-whatsapp text-white text-center py-3 rounded-lg flex items-center justify-center mr-2'
                >
                  <FaWhatsapp className='mr-2 text-xl' />
                  WhatsApp
                </a>

                <a
                  href={`tel:${user?.phone}`}
                  className='w-1/2 bg-redpink text-white text-center py-3 rounded-lg flex items-center justify-center'
                >
                  <FaPhone className='mr-2 text-xl' />
                  {formatBelgianPhoneNumber(user?.phone ?? '')}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='max-w-7xl mx-2 lg:mx-auto my-2'>
        {errorKey ? (
          <div className='flex items-center justify-center min-h-72 p-5 bg-grisclair rounded-lg'>
            <div className='text-center'>
              <IoIosWarning className='text-redpink text-3xl md:text-4xl lg:text-6xl mx-auto' />
              <h2 className='text-xl lg:text-2xl font-semibold text-anthracite'>
                {t('ad-not-found')}
              </h2>
              <p className='text-gray-600 text-sm lg:text-base'>
                {t(errorKey)}
              </p>
              <div className='space-x-0 sm:space-x-2 mt-4'>
                <Link
                  to='/'
                  className='bg-redpink text-white px-6 py-2 rounded-lg hover:bg-anthracite transition-all inline-flex items-center'
                >
                  <AiFillHome className='mr-2' />
                  {t('dashboard.to-the-site')}
                </Link>
                <button
                  onClick={() => window.location.reload()}
                  className='bg-anthracite mt-2 sm:mt-0 text-white px-6 py-2 rounded-lg hover:bg-redpink transition-all inline-flex items-center'
                >
                  <IoMdRefresh className='mr-2' />
                  {t('reload-page')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          announce && (
            <div className='block md:flex relative'>
              <div className='w-full md:w-2/5 relative'>
                <Gallery
                  gender={user?.userProfile.gender}
                  gallery={gallery}
                  coverImage={coverImage}
                  statusToShow={1}
                />
                {gallery.length > 1 && (
                  <div className='mt-4 flex flex-wrap gap-2 justify-between'>
                    {gallery.map((image, index) => (
                      <img
                        key={index}
                        src={image.fileUrl}
                        alt={`Image ${index + 1}`}
                        className='w-20 h-20 object-cover cursor-pointer rounded hover:opacity-80'
                        onClick={() => {
                          setCurrentImageIndex(index)
                          setIsGalleryModalOpen(true)
                        }}
                      />
                    ))}
                  </div>
                )}
                {announce.escort && (
                  <div className='hidden md:block'>
                    <div className='lg:text-lg font-light flex justify-center mt-2 md:mt-5 bg-anthracite text-white rounded-t py-1'>
                      {t('leaving-from')}
                      <span className='text-redpink uppercase font-bold ml-2'>
                        {user?.address.city}
                      </span>
                    </div>
                    <div
                      className='relative bg-grisclair rounded-b'
                      onMouseLeave={onMouseLeave}
                    >
                      <Map
                        selectedProvinces={announce.location}
                        onProvinceHover={onProvinceHover}
                        setSelectedProvinces={setSelectedProvinces}
                      />
                      {tooltip && <Tooltip province={tooltip.province} />}
                    </div>
                  </div>
                )}
              </div>
              <div className='w-full md:w-3/5 md:ml-7 mt-5 sm:mt-0'>
                <p className='font-semibold text-xl lg:text-2xl'>
                  {announce.title}
                </p>
                <div className='flex'>
                  {[...new Set(announce.status)].map((status, index) => (
                    <Link
                      key={index}
                      to={`/${status}/`}
                      className='flex items-center justify-center bg-redpink text-white px-2 py-0 mr-2 mt-1 rounded hover:bg-anthracite transition-all'
                    >
                      {t(`create-ad.${status}`)}
                    </Link>
                  ))}
                  <Link
                    className='flex bg-redpink text-white px-2 py-0 mr-2 mt-1 rounded hover:bg-anthracite transition-all'
                    to='/'
                    onClick={handleLinkClick}
                  >
                    <FaLocationDot className='mr-1 mt-1 text' />
                    {t(`${user?.address?.city}`)}
                  </Link>
                </div>

                {user?.verifiedStatus === 'verified' ? (
                  <span className='inline-flex items-center justify-center bg-emerald-500 text-white px-2 py-0 mr-2 mt-1 rounded hover:bg-anthracite transition-all'>
                    <MdCheckCircle
                      className='mr-1 mt-0.1 text'
                      title={t('verified')}
                    />
                    {t('verified-user')}
                  </span>
                ) : null}

                <p className='my-5 whitespace-pre-line text-sm sm:text-base'>
                  {announce.description}
                </p>
                <div className='border border-1 border-anthracite overflow-hidden rounded-lg'>
                  <p className='text-lg bg-anthracite text-white rounded-t text-center py-1'>
                    {t('contact')}
                  </p>
                  <div className='block xl:flex'>
                    <div
                      className={`${
                        announce.isAvailable ||
                        (Array.isArray(announce.disponibility) &&
                          announce.disponibility.length > 0)
                          ? 'full xl:w-1/2'
                          : 'w-full'
                      }`}
                    >
                      <div className='flex hover:bg-grisclair justify-between py-2  px-4'>
                        <a
                          href={`tel:${user?.phone}`}
                          className='flex justify-between w-full'
                        >
                          <span className='flex items-center'>
                            <FaPhone className='text-redpink mr-2 text-xl' />
                            <span>{t('phone')}</span>
                          </span>
                          <span className='text-redpink'>
                            {formatBelgianPhoneNumber(user?.phone ?? '')}
                          </span>
                        </a>
                      </div>
                      {announce?.whatsapp && (
                        <div className='flex hover:bg-grisclair justify-between py-2  px-4'>
                          <a
                            href={`https://wa.me/${user?.phone}`}
                            className='flex justify-between w-full'
                          >
                            <span className='flex items-center'>
                              <FaWhatsapp className='text-whatsapp text-xl mr-2' />
                              <span>WhatsApp</span>
                            </span>
                            <span className='text-redpink'>
                              {formatBelgianPhoneNumber(user?.phone ?? '')}
                            </span>
                          </a>
                        </div>
                      )}
                      {announce.website && (
                        <div className='flex hover:bg-grisclair justify-between py-2 px-4'>
                          <a
                            href={
                              announce.website.startsWith('http')
                                ? announce.website
                                : `http://${announce.website}`
                            }
                            className='flex justify-between w-full'
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            <span className='flex items-center'>
                              <FaGlobe className='text-redpink text-xl mr-2' />
                              <span>{t('website')}</span>
                            </span>
                            <span className='text-redpink'>
                              {announce.website}
                            </span>
                          </a>
                        </div>
                      )}
                      {announce.callOnly && (
                        <p className='hover:bg-grisclair flex justify-center py-2 px-4'>
                          <span>
                            <FaPhone className='mr-2' />
                          </span>
                          {t('create-ad.call-only')}
                        </p>
                      )}
                      {announce.smsOnly && (
                        <p className='hover:bg-grisclair flex justify-center py-2 px-4'>
                          <span>
                            <FaSms className='mr-2' />
                          </span>
                          {t('create-ad.sms-only')}
                        </p>
                      )}
                    </div>
                    {(announce.isAvailable ||
                      (Array.isArray(announce.disponibility) &&
                        announce.disponibility.length > 0)) && (
                      <div className='w-full xl:w-1/2'>
                        {announce.isAvailable ? (
                          <div className='w-full text-right'>
                            <p className='hover:bg-grisclair flex justify-between py-2  px-4'>
                              <span className='text-center w-full'>
                                {t('create-ad.available247')}
                              </span>
                            </p>
                          </div>
                        ) : (
                          Array.isArray(announce.disponibility) &&
                          announce?.disponibility?.map(
                            (day: string, index: number) => {
                              const currentDay = new Date().getDay()
                              const isDayToday =
                                dayMap[day.toLowerCase()] === currentDay
                              const isOpen =
                                announce.availabilityStart?.[index] !==
                                  '00:00' &&
                                announce.availabilityEnd?.[index] !== '00:00'

                              return (
                                <div
                                  key={`availability-${day}-${index}`}
                                  className='w-full text-right'
                                >
                                  <p className='flex justify-between py-2  px-4 hover:bg-grisclair'>
                                    <span
                                      className={`flex items-center ${
                                        isDayToday ? 'font-bold' : ''
                                      }`}
                                    >
                                      <span>
                                        {t(`create-ad.${day.toLowerCase()}`)}
                                      </span>
                                    </span>
                                    <span
                                      className={`text-redpink ${
                                        isOpen ? '' : 'text-gray-400'
                                      }`}
                                    >
                                      {announce.availabilityStart &&
                                      announce.availabilityEnd &&
                                      announce.availabilityStart[index] ===
                                        '00:00' &&
                                      announce.availabilityEnd[index] ===
                                        '00:00' ? (
                                        t('24h/24')
                                      ) : (
                                        <span className='flex'>
                                          {isDayToday && (
                                            <span className='text-green-500 mr-2 mt-0.5'>
                                              {isWithinTimeRange(
                                                new Date().toLocaleTimeString(
                                                  'en-US',
                                                  {
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    hour12: false,
                                                  },
                                                ),
                                                announce.availabilityStart?.[
                                                  index
                                                ] || '',
                                                announce.availabilityEnd?.[
                                                  index
                                                ] || '',
                                              ) && <MdCheckCircle />}
                                            </span>
                                          )}
                                          {announce.availabilityStart?.[
                                            index
                                          ] ?? ''}
                                          <span className='px-1'>
                                            {t('to')}
                                          </span>
                                          {announce.availabilityEnd?.[index] ??
                                            ''}
                                        </span>
                                      )}
                                    </span>
                                  </p>
                                </div>
                              )
                            },
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className='border border-1 border-anthracite rounded-lg mt-5'>
                  <p className='text-lg bg-anthracite text-white rounded-t text-center py-1'>
                    {t('features')}
                  </p>
                  <div>
                    <div className='w-full grid grid-cols-2 lg:grid-cols-3 text-center'>
                      {userProfileFields.map(field => {
                        const value =
                          user?.userProfile[field.key as keyof UserProfile]
                        return value && value !== '' ? (
                          <div
                            key={field.key}
                            className='w-full p-3 hover:bg-grisclair rounded-lg'
                          >
                            <p className='uppercase font-light'>
                              {t(field.label)}
                            </p>
                            <p className='font-semibold text-sm'>
                              {Array.isArray(value)
                                ? value
                                    .map(item =>
                                      t(
                                        `${
                                          field.prefix || 'create-ad'
                                        }.${item}`,
                                      ),
                                    )
                                    .join(', ')
                                : field.usePrefix
                                ? t(`${field.prefix || 'create-ad'}.${value}`)
                                : value}
                            </p>
                          </div>
                        ) : null
                      })}
                    </div>
                  </div>
                </div>

                {announce?.practices &&
                announce.practices.length > 0 &&
                practiceData.length > 0 ? (
                  <div className='border mt-5 border-1 border-anthracite rounded-lg pb-2'>
                    <p className='text-lg bg-anthracite text-white rounded-t text-center py-1'>
                      {t('services')}
                    </p>
                    {practiceData.map(category => {
                      const filteredPractices = category.practices.filter(
                        practice => announce?.practices.includes(practice.id),
                      )
                      return filteredPractices.length > 0 ? (
                        <div className='p-2 pb-0' key={category.id}>
                          <div className='w-full text-center bg-grisclair2 rounded-lg mb-2 uppercase p-1 flex justify-center'>
                            {category.id === 6 && (
                              <TbDisabled2 className='mr-2 text-xl text-blue-500' />
                            )}
                            {t(`create-ad.${category.categoryName}`)}
                          </div>
                          <div className='w-full grid grid-cols-2 lg:grid-cols-3 text-center'>
                            {filteredPractices.map(practice => (
                              <div
                                className='relative px-2 py-3 text-sm font-semibold hover:bg-grisclair rounded-lg'
                                key={practice.id}
                              >
                                {t(`practices.${practice.practiceName}`)}
                                {hasPracticesWarnings(
                                  practice.practiceName,
                                ) && (
                                  <PracticesWarnings
                                    practiceName={practice.practiceName}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null
                    })}
                  </div>
                ) : null}
                {hasPrices && (
                  <div className='border mt-5 border-1 border-anthracite rounded-lg'>
                    <p className='text-lg bg-anthracite text-white rounded-t text-center py-1'>
                      {t('private-prices')}
                    </p>
                    <div className='w-full grid grid-cols-1 gap-2 p-2   text-center'>
                      {announce.timing.map((time, index) => (
                        <div
                          key={index}
                          className='w-full flex justify-center items-center'
                        >
                          <span className=' w-5/6 p-2 bg-grisclair hover:bg-grisclair2 rounded-l text-sm md:text-base text-left'>
                            {t(`create-ad.${time}`)}
                          </span>
                          <span className='w-1/6 px-2 py-1.5 bg-redpink text-white rounded-r text-sm md:text-base lg:text-lg'>
                            {announce.price[index]}€
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {hasEscortPrices && (
                  <div className='border mt-5 border-1 border-anthracite rounded-lg'>
                    <p className='text-lg bg-anthracite text-white rounded-t text-center py-1'>
                      {t('escort-prices')}
                    </p>
                    <div className='w-full grid grid-cols-1 gap-2 p-2   text-center'>
                      {announce.timingEscort.map((time, index) => (
                        <div
                          key={index}
                          className='w-full flex justify-center items-center'
                        >
                          <span className=' w-5/6 p-2 bg-grisclair hover:bg-grisclair2 rounded-l text-sm md:text-base text-left'>
                            {t(`create-ad.${time}`)}
                          </span>
                          <span className='w-1/6 px-2 py-1.5 bg-redpink text-white rounded-r text-sm md:text-base lg:text-lg'>
                            {announce.priceEscort[index]}€
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        )}
      </div>
      {tooltip && <Tooltip province={tooltip.province} />}
      {user?.email && (
        <SendMailModal
          isOpen={isSendMailModalOpen}
          onClose={() => setIsSendMailModalOpen(false)}
          recipientEmail={user.email}
          recipientName={user.nickname ?? ''}
        />
      )}
      {user && (
        <ReportUserModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          userNickname={user.nickname}
          userId={user.id}
        />
      )}
      {isGalleryModalOpen && (
        <div className='fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center'>
          <button
            className='absolute top-5 right-5 text-white text-3xl'
            onClick={() => setIsGalleryModalOpen(false)}
          >
            &times;
          </button>

          <button
            onClick={() =>
              setCurrentImageIndex(prev =>
                prev === 0 ? gallery.length - 1 : prev - 1,
              )
            }
            className='absolute left-4 text-white text-4xl'
          >
            &#10094;
          </button>

          <img
            src={gallery[currentImageIndex].fileUrl}
            alt={`Image ${currentImageIndex + 1}`}
            className='max-w-[90%] max-h-[90%] rounded-lg shadow-lg'
          />

          <button
            onClick={() =>
              setCurrentImageIndex(prev =>
                prev === gallery.length - 1 ? 0 : prev + 1,
              )
            }
            className='absolute right-4 text-white text-4xl'
          >
            &#10095;
          </button>
        </div>
      )}
    </div>
  )
}

export default OneAnnounce
